import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import BulkEmailTable from '../../../components/tables/BulkEmailTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { BulkEmailClient, BulkEmailDTO, SearchRequestDTO_bulkEmailSearchRequestDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/config';
import axiosInstance from '../../../services/axiosInstance';
import AddNewButton from '../../../components/common/AddButton';
import {confirmDelete} from "../../../utils/sweetAlertUtils";

const BulkEmailList: React.FC = () => {
    const [bulkEmails, setBulkEmails] = useState<BulkEmailDTO[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [subjectSearch, setSubjectSearch] = useState('');

    const navigate = useNavigate();
    const breadcrumbs = [
        { label: 'Admin', href: '#' },
        { label: 'Bulk Emails', href: '#' },
    ];

    const apiClient = new BulkEmailClient(API_BASE_URL, axiosInstance);

    const fetchBulkEmails = async (page: number, size: number, filters = {}) => {
        const searchRequest: SearchRequestDTO_bulkEmailSearchRequestDTO = {
            filters: {
                subject: subjectSearch || '',
                ...filters,
            },
            page,
            pageSize: size,
            sortBy: 'SentDate',
            sortDescending: true,
        };

        try {
            setIsLoading(true);
            const response = await apiClient.searchBulkEmails(searchRequest);
            const totalRecords = response.data?.totalCount ?? 0;

            setBulkEmails(response.data?.results as BulkEmailDTO[] || []);
            setTotalRecords(totalRecords);
            setTotalPages(Math.ceil(totalRecords / size));
        } catch (error) {
            console.error('Error fetching bulk emails:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBulkEmails(currentPage, pageSize);
    }, [currentPage, pageSize]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchBulkEmails(1, pageSize);
    };

    const handleClearSearch = () => {
        setSubjectSearch('');
        fetchBulkEmails(1, pageSize, {
            subject: '',
        });
    };

    const handleProcessSend = async (dto: BulkEmailDTO) => {
        try {
            await apiClient.processBulkEmail(dto.id || '');
            await fetchBulkEmails(currentPage, pageSize, { subjectSearch });
        } catch (error) {
            console.error('Error processing bulk email:', error);
        }
    };

    const handleDelete = async (dto: BulkEmailDTO) => {
        const confirmed = await confirmDelete();
        if (!confirmed) return;

        try {
            await apiClient.deleteBulkEmail(dto.id || '');
            await fetchBulkEmails(currentPage, pageSize, { subjectSearch });
        } catch (error) {
            console.error('Error deleting:', error);
        }
    };

    return (
        <AdminLayout
            title="Bulk Emails"
            breadcrumbs={breadcrumbs}
            allowedRoles={['Administrator']}
        >
            <Card>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-500 dark:text-gray-300">
                        Manage Bulk Emails ({totalRecords})
                    </h2>
                    <div className="ml-auto">
                        <AddNewButton onClick={() => navigate('/admin/bulkemails/create')} />
                    </div>
                </div>

                <form onSubmit={handleSearch} className="mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <FormField
                            id="subjectSearch"
                            label="Subject"
                            value={subjectSearch}
                            onChange={(e) => setSubjectSearch(e.target.value)}
                            placeholder="Search by subject"
                        />
                    </div>

                    <div className="flex space-x-4 mb-4">
                        <Button variant="secondary" onClick={handleClearSearch}>
                            Clear Search
                        </Button>
                        <Button type="submit">Search</Button>
                    </div>
                </form>

                <BulkEmailTable
                    emails={bulkEmails}
                    handleEdit={(dto) =>
                        navigate(`/admin/bulkemails/${dto.id}`)
                    }
                    handleDelete={handleDelete}
                    handleProcessSend={handleProcessSend}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    isLoading={isLoading}
                />
            </Card>
        </AdminLayout>
    );
};

export default BulkEmailList;
